import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Calendar, Image as ImageIcon, AlertCircle, ArrowLeft } from 'lucide-react';
import api from '../../../api/axios';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Skeleton from 'react-loading-skeleton';

export default function PrescriptionList() {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  const fetchPrescriptions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/prescriptions/');
      
      if (response.data.success) {
        setPrescriptions(response.data.data);
      } else {
        setError('Failed to load prescriptions');
      }
    } catch (error) {
      console.error('Error fetching prescriptions:', error);
      setError('Failed to load prescriptions');
    } finally {
      setLoading(false);
    }
  };

  const deletePrescription = async (id) => {
    if (!window.confirm('Are you sure you want to delete this prescription?')) {
      return;
    }

    setDeleting(id);
    try {
      const response = await api.delete(`/prescriptions/${id}/delete/`);
      
      if (response.data.success) {
        setPrescriptions(prescriptions.filter(p => p.id !== id));
      } else {
        setError('Failed to delete prescription');
      }
    } catch (error) {
      console.error('Error deleting prescription:', error);
      setError('Failed to delete prescription');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-semibold transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            Back to Profile
          </button>

          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                My Prescriptions
              </h1>
              <p className="text-gray-600 mt-1">
                Manage your uploaded prescriptions
              </p>
            </div>
            <button
              onClick={() => navigate('/prescriptions/upload')}
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Upload New</span>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm p-4">
                  <Skeleton height={200} className="mb-4" />
                  <Skeleton height={20} className="mb-2" />
                  <Skeleton height={16} />
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && prescriptions.length === 0 && (
            <div className="text-center py-12">
              <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No prescriptions uploaded
              </h3>
              <p className="text-gray-500 mb-6">
                Upload your first prescription to get started
              </p>
              <button
                onClick={() => navigate('/prescriptions/upload')}
                className="inline-flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                <span>Upload Prescription</span>
              </button>
            </div>
          )}

          {/* Prescriptions Grid */}
          {!loading && prescriptions.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {prescriptions.map((prescription) => (
                <div key={prescription.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                  {/* Image */}
                  <div className="aspect-w-3 aspect-h-2 bg-gray-100">
                    <img
                      src={`https://www.homecureexpress.com${prescription.image}`}
                      alt="Prescription"
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden w-full h-48 bg-gray-100 items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-2">
                          Prescription #{prescription.id}
                        </h3>
                        <div className="flex items-center space-x-1 text-sm text-gray-500">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(prescription.uploaded_at)}</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => deletePrescription(prescription.id)}
                        disabled={deleting === prescription.id}
                        className={`p-2 rounded-lg transition-colors ${
                          deleting === prescription.id
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                        }`}
                      >
                        {deleting === prescription.id ? (
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>

                    {/* Action Button */}
                    <div className="mt-4">
                      <button
                        onClick={() => window.open(`https://www.homecureexpress.com${prescription.image}`, '_blank')}
                        className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                      >
                        View Full Size
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Help Text */}
          {!loading && prescriptions.length > 0 && (
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">
                📋 Next Steps
              </h4>
              <p className="text-sm text-blue-800">
                After uploading prescriptions, our pharmacists will review them and contact you 
                for confirmation before processing your order. You can also browse and order 
                medicines directly from our catalog.
              </p>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}